<script setup lang="ts">
  import { testIds, type StakeholderContactPerson } from '@gat-ship/types'
  import { useFormatting } from '@gat-ship/utils'
  import {
    DataListItem,
    DataListItemAction,
    DataListItemActions,
    DataListItemDescription,
    DataListItemTitle,
    DropdownMenu,
    DropdownMenuTrigger,
    DropdownMenuContent,
    DropdownMenuItem
  } from '@gat-ship/vue-components'
  import ContactDetailsBtnShow from '../commands/buttons/ContactDetailsBtnShow.vue'
  import ContactPersonBtnDelete from '../commands/buttons/ContactPersonBtnDelete.vue'
  import { useContactProvider } from '../../../composables/useStakeholderContacts'
  import {
    IconError,
    IconEye,
    IconEdit,
    IconTrash,
    IconMail,
    IconSmartphone,
    IconPhone
  } from '@gat-ship/icons'

  const props = withDefaults(
    defineProps<{
      contact: StakeholderContactPerson
      showContactType?: boolean
      errorMessages?: string[]
    }>(),
    {
      showContactType: false,
      errorMessages: () => []
    }
  )

  const { formatPhoneNumber } = useFormatting()

  const emit = defineEmits<{
    (e: 'click:item', contact: StakeholderContactPerson): void
  }>()

  // Null fallback (not the throwing default): this row also renders inside
  // QuickContacts in the create-stakeholder drawer, which has no
  // ContactsProvider. There the default #actions dropdown is slot-overridden,
  // so `modals` is only ever read when a provider is actually present.
  const provider = useContactProvider(null)
</script>
<template>
  <DataListItem
    :item="props.contact"
    :options="{
      clickable: true
    }"
    :data-testid="
      testIds.stakeholders.contacts.list.contactPersonId(props.contact.id)
    "
    @click="emit('click:item', props.contact)"
  >
    <div class="flex grow flex-col items-start justify-center gap-0.5">
      <DataListItemTitle class="flex flex-row items-center gap-1">
        <span
          v-if="props.contact.firstName"
          :data-testid="
            testIds.stakeholders.contacts.list.contactPersonFirstName(
              props.contact.firstName ?? ''
            )
          "
        >
          {{ props.contact.firstName }}
        </span>
        <span
          v-if="props.contact.lastName"
          :data-testid="
            testIds.stakeholders.contacts.list.contactPersonLastName(
              props.contact.lastName ?? ''
            )
          "
        >
          {{ props.contact.lastName }}
        </span>
        <template v-if="showContactType">
          <span class="text-muted-foreground text-xs">•</span>
          <span class="text-muted-foreground text-xs font-normal"
            >Contact Person</span
          >
        </template>
      </DataListItemTitle>
      <DataListItemDescription
        v-if="props.contact.jobTitle || props.contact.department"
        class="space-x-1 text-xs leading-none"
      >
        <span
          v-if="props.contact.jobTitle"
          :data-testid="
            testIds.stakeholders.contacts.list.contactPersonJobTitle(
              props.contact.jobTitle ?? ''
            )
          "
        >
          {{ props.contact.jobTitle }}
        </span>
        <span v-if="props.contact.jobTitle && props.contact.department">-</span>
        <span
          v-if="props.contact.department"
          :data-testid="
            testIds.stakeholders.contacts.list.contactPersonDepartment(
              props.contact.department ?? ''
            )
          "
        >
          {{ props.contact.department }}
        </span>
      </DataListItemDescription>

      <div
        v-if="
          props.contact.email ||
          props.contact.mobilePhone ||
          props.contact.directLine
        "
        class="mt-2 flex flex-col gap-1 text-xs"
      >
        <div v-if="props.contact.email" class="flex items-center gap-1">
          <icon-mail class="size-3.5" />
          <span
            class="truncate"
            :data-testid="
              testIds.stakeholders.contacts.list.contactPersonEmail(
                props.contact.email ?? ''
              )
            "
          >
            {{ props.contact.email }}
          </span>
        </div>
        <div v-if="props.contact.mobilePhone" class="flex items-center gap-1">
          <icon-smartphone class="size-3.5" />
          <span
            class="truncate"
            :data-testid="
              testIds.stakeholders.contacts.list.contactPersonMobilePhone(
                props.contact.mobilePhone ?? ''
              )
            "
            >{{ formatPhoneNumber(props.contact.mobilePhone) }}</span
          >
        </div>
        <div v-if="props.contact.directLine" class="flex items-center gap-1">
          <icon-phone class="size-3.5" />
          <span
            class="truncate"
            :data-testid="
              testIds.stakeholders.contacts.list.contactPersonDirectLine(
                props.contact.directLine ?? ''
              )
            "
          >
            {{ formatPhoneNumber(props.contact.directLine) }}
          </span>
        </div>
      </div>
      <div
        v-if="errorMessages.length > 0"
        class="bg-error/10 border-error/20 mt-2 flex items-start gap-1 rounded-md p-2 pr-3"
      >
        <IconError class="size-5" />
        <span class="text-muted-foreground whitespace-pre-line text-xs">
          {{ errorMessages.join('\r\n') }}
        </span>
      </div>
    </div>
    <template #actions>
      <slot name="actions">
        <DataListItemActions>
          <DropdownMenu>
            <DropdownMenuTrigger as-child>
              <DataListItemAction
                :test-id="
                  testIds.stakeholders.contacts.list.actions.dropdownBtn(
                    props.contact.id
                  )
                "
              />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <ContactDetailsBtnShow
                :contact="props.contact"
                label="Show Details"
                :icon="IconEye"
                :stakeholder-id="props.contact.stakeholderId"
                :stakeholder-name="props.contact.stakeholderName"
                :stakeholder-short-name="props.contact.stakeholderShortName"
                :stakeholder-type="props.contact.stakeholderType"
                :data-testid="
                  testIds.stakeholders.contacts.list.actions.showContactDetails
                "
              />
              <DropdownMenuItem
                :data-testid="
                  testIds.stakeholders.contacts.list.actions.editContactPerson
                "
                @click="
                  provider?.modals.showEditContactModal({
                    stakeholderId: props.contact.stakeholderId,
                    stakeholderName: props.contact.stakeholderName,
                    stakeholderShortName: props.contact.stakeholderShortName,
                    stakeholderType: props.contact.stakeholderType,
                    contactType: 'person',
                    contact: props.contact
                  })
                "
              >
                <IconEdit class="size-4" />
                Edit
              </DropdownMenuItem>
              <ContactPersonBtnDelete
                :contact="props.contact"
                label="Delete"
                :icon="IconTrash"
                :data-testid="
                  testIds.stakeholders.contacts.list.actions.deleteContactPerson
                "
              />
            </DropdownMenuContent>
          </DropdownMenu>
        </DataListItemActions>
      </slot>
    </template>
  </DataListItem>
</template>
